import React, { useState } from "react";
import { useSelector } from "react-redux";
import { fetchTheFlights } from "../../../Redux/Apis/Flight-Apis";
import SearchExplainer from "./SearchExplainer/SearchExplainer";
import "./SearchModePopup.css";

/**
 * Admin-only popup shown when clicking SEARCH — both on the Homepage
 * (SearchBarHomePage/SpecialFareOptions.jsx) and the Results page's own search bar
 * (ResultPages/Flights/SearchBar/SearchBarResultPage/*SearchBar.jsx). Deliberately does NOT
 * touch either page's own (large, load-bearing) searchTheFlightUrlCreator reducer — those
 * still drive the real customer-facing search, completely unchanged.
 *
 * Callers hand in `buildParams: () => object`, which must return the exact
 * Travellers/User/Fare/Segments shape MainSearchController::search() validates (no
 * SearchMode key — this component adds that itself). Each page builds it differently: the
 * Homepage constructs it fresh from its own searchParams state (see SpecialFareOptions.jsx),
 * while the Results page already carries that exact shape in state.SearchParameters and just
 * passes it straight through.
 *
 * - "Direct Search": closes the popup and runs the real search immediately.
 * - "Test Search": calls the backend with SearchMode: "test-mode" (see MainSearchController's 6
 *   handlers) and renders the debug JSON (activeApiCodesData/finalParsedData/requestStruct)
 *   through <SearchExplainer> — a human-readable Admin (tables) / Developer (narrative + raw
 *   JSON) report — instead of navigating anywhere.
 * - "Confirm — Run Real Search" (shown once test results are in): closes the popup and runs the
 *   real search immediately, same as "Direct Search".
 *
 * Per explicit instruction ("caching wali cheez hatani hai bas") — the admin-chosen cache-
 * duration step this popup used to show between choosing a search mode and it actually running
 * has been removed; every search (admin or not) now just uses each handler's own default cache
 * TTL, same as a normal customer search always has.
 */
function SearchModePopup({ onClose, onDirectSearch, buildParams }) {
  const [view, setView] = useState("choice"); // 'choice' | 'loading' | 'result' | 'error'
  const [testResult, setTestResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // This popup only ever renders for a logged-in admin (see the isAdminLogin gate at each of
  // its 4 callers), so this is always set here — included anyway for parity with
  // SearchTheFlights' own AdminId tagging (ResultPage-Flights-SLICE.js).
  const adminId = useSelector((state) => state.user.userLoggedInData?.id);

  const handleTestSearch = async () => {
    setView("loading");
    setErrorMessage("");

    try {
      const response = await fetchTheFlights({
        ...buildParams(),
        SearchMode: "test-mode",
        ...(adminId ? { AdminId: adminId } : {}),
      });
      setTestResult(response?.data ?? null);
      setView("result");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Test search failed — check console for details."
      );
      console.error("Test search error:", error);
      setView("error");
    }
  };

  const handleRunRealSearch = () => {
    onClose();
    onDirectSearch();
  };

  return (
    <div className="search-mode-popup-overlay" onClick={onClose}>
      <div
        className={`search-mode-popup-box ${
          view === "result" ? "search-mode-popup-box-expanded" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="search-mode-popup-header">
          <span>Admin Search Mode</span>
          <span className="search-mode-popup-close" onClick={onClose}>
            ×
          </span>
        </div>

        {view === "choice" && (
          <div className="search-mode-popup-choices">
            <button
              className="search-mode-popup-btn search-mode-popup-btn-direct"
              onClick={handleRunRealSearch}
            >
              Direct Search
            </button>
            <button
              className="search-mode-popup-btn search-mode-popup-btn-test"
              onClick={handleTestSearch}
            >
              Test Search
            </button>
          </div>
        )}

        {view === "loading" && (
          <div className="search-mode-popup-status">Running test search…</div>
        )}

        {view === "error" && (
          <div className="search-mode-popup-status search-mode-popup-status-error">
            {errorMessage}
          </div>
        )}

        {view === "result" && (
          <div className="search-mode-popup-result-wrap">
            <SearchExplainer data={testResult} />
            <button
              className="search-mode-popup-btn search-mode-popup-btn-confirm"
              onClick={handleRunRealSearch}
            >
              Confirm — Run Real Search
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchModePopup;
